import { createSelector, createSlice } from '@reduxjs/toolkit';

import { factsFromVenue } from '../components/SearchVenues/HostView/venueFacts';

const initialState = {
  form: {
    name: '',
    description: '',
    setting: 'outdoor',
    venueType: 'home',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    address_country: 'US', // ISO code (DS-G2, venue-search VS1.3)
    additionalNotes: '',
    amenities: [],
    dayPrice: '',
    maxGuests: '',
    cancellationPolicy: 'flexible', // flexible, moderate, strict
    photos: [], // [{ url, ref }]
    disabled: false,
    verified: false,
    excludedDates: [],
    userId: '', // billing B1.5: whose Sunday choice applies (admin review); never saved by this form
    allowPayAnotherAmount: false,
    porcentageAmountToPayCustom: 0,
    // Venue-search VS1.3: the new facts, a flat map of field-dictionary ID -> value
    // (venueFacts.js). Never saved as one field.
    facts: {},
    estimated: {}, // field IDs the square footage helper filled (not saved)
    eventTypesRestricted: undefined, // the Yes/No before the event-type list (not saved)
    contactName: '', // on-site contact: saved in the private record, never on the venue
    contactPhone: ''

    // paymentOptions: ['100%']
  },
  formErrors: {},
  uploadError: '',
  geoErrors: false,
  saveError: false,
  isSaving: false,
  isUploading: false
};

// Form-only values: never read from a venue record.
const LOCAL_FIELDS = ['facts', 'estimated', 'eventTypesRestricted', 'contactName', 'contactPhone'];

const ERROR_KEYS = {
  contactName: 'contact.onSite',
  contactPhone: 'contact.onSite',
  eventTypesRestricted: 'rules.eventTypesNotAllowed'
};

const search = createSlice({
  name: 'hostedVenue',
  initialState,
  reducers: {
    // useful if editing an existing record
    setInitialState(state, action) {
      state.formErrors = {}; // reset
      state.uploadError = '';
      state.geoErrors = false;
      state.saveError = false;
      state.isSaving = false;
      state.isUploading = false;
      // start form off empty
      const { form: emptyForm } = initialState;
      state.form = { ...emptyForm };
      // add venue state if fields overlap with form
      const venueState = action.payload;
      if (venueState) {
        for (let name in emptyForm) {
          // grab address from existing venue, if exists
          if (name.startsWith('address_')) {
            const field = name.replace('address_', '');
            if (venueState.address && venueState.address[field]) {
              state.form[name] = venueState.address[field];
            }
          } else if (venueState.hasOwnProperty(name) && !LOCAL_FIELDS.includes(name)) {
            state.form[name] = venueState[name];
          }
        }
        state.form.address_country = 'US';
        state.form.facts = factsFromVenue(venueState);
        const restricted = state.form.facts['rules.eventTypesNotAllowed'];
        if (restricted !== undefined) {
          state.form.eventTypesRestricted = Array.isArray(restricted) ? restricted.length > 0 : true;
        }
      }
    },
    // One fact (field-dictionary ID). value undefined = not answered.
    // clear (VS1.4): more error keys to remove, e.g. ["inventory.0.count"], or a
    // prefix ending in "." to remove every error under it (rows moved).
    setFact(state, action) {
      const { id, value, estimated, clear } = action.payload;
      if (typeof clear === 'string') Object.keys(state.formErrors).forEach((key) => key.startsWith(clear) && delete state.formErrors[key]);
      else (clear || []).forEach((key) => delete state.formErrors[key]);
      if (value === undefined) delete state.form.facts[id];
      else state.form.facts[id] = value;
      if (estimated) state.form.estimated[id] = true;
      else delete state.form.estimated[id];
      delete state.formErrors[id];
    },
    // The private record of an old venue, read after the form opens.
    setPrivateRecord(state, action) {
      const contact = action.payload?.contact?.onSite;
      if (contact && !state.form.contactName && !state.form.contactPhone) {
        state.form.contactName = contact.name || '';
        state.form.contactPhone = contact.phone || '';
      }
    },
    setFormValue(state, action) {
      Object.assign(state.form, action.payload);
      // remove validation error if exists
      for (let name in action.payload) {
        if (state.formErrors[name]) {
          delete state.formErrors[name];
        }
        // VS1.3: errors keyed by the field-dictionary ID these form values feed.
        const factId = ERROR_KEYS[name];
        if (factId) delete state.formErrors[factId];
        if (name === 'photos') {
          state.uploadError = '';
          state.isUploading = false;
        }
        if (name.startsWith('address') && state.geoErrors) {
          state.geoErrors = false;
        }
      }
    },
    // Adds to the current list, so uploads that finish together never overwrite each other.
    addPhotos(state, action) {
      state.form.photos.push(...action.payload);
      delete state.formErrors.photos;
      state.uploadError = '';
    },
    // Moves one photo to another photo's place. Reads the current list, so an upload
    // that finishes during a drag is never lost. Photos are found by url.
    movePhoto(state, action) {
      const { photos } = state.form;
      const from = photos.findIndex((photo) => photo.url === action.payload.from);
      const to = photos.findIndex((photo) => photo.url === action.payload.to);
      if (from < 0 || to < 0 || from === to) return;
      photos.splice(to, 0, photos.splice(from, 1)[0]);
    },
    setFormErrors(state, action) {
      state.formErrors = action.payload || {};
      state.isSaving = false;
    },
    setUploadError(state, action) {
      state.uploadError = action.payload || '';
      state.isUploading = false;
    },
    setGeoErrors(state, action) {
      state.geoErrors = action.payload === true;
      state.isSaving = false;
    },
    setSaveError(state, action) {
      state.saveError = action.payload === true;
      state.isSaving = false;
    },
    setIsSaving(state, action) {
      state.isSaving = action.payload === true;
      state.formErrors = {};
      state.uploadError = '';
      state.geoErrors = false;
      state.saveError = false;
    },
    setIsUploading(state, action) {
      state.isUploading = action.payload === true;
    }
  }
});

export default search.reducer;

export const {
  setInitialState,
  setFact,
  setPrivateRecord,
  setFormValue,
  addPhotos,
  movePhoto,
  setFormErrors,
  setUploadError,
  setGeoErrors,
  setSaveError,
  setIsSaving,
  setIsUploading
} = search.actions;

////////////////////////////
//   Selector Functions   //
////////////////////////////

const selectSelf = (state) => state.hostedVenue;

export const getForm = createSelector(selectSelf, ({ form }) => form);
export const getFormErrors = createSelector(selectSelf, ({ formErrors }) => formErrors);
export const getUploadError = createSelector(selectSelf, ({ uploadError }) => uploadError);
export const getGeoErrors = createSelector(selectSelf, ({ geoErrors }) => geoErrors);
export const getSaveError = createSelector(selectSelf, ({ saveError }) => saveError);
export const getIsSaving = createSelector(selectSelf, ({ isSaving }) => isSaving);
export const getIsUploading = createSelector(selectSelf, ({ isUploading }) => isUploading);
